import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

const DISMISS_KEY = "signup-popup-dismissed-v1";

export function SignupPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    let timer: number | undefined;
    let cancelled = false;

    (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data } = await supabase.auth.getSession();
        if (cancelled || data.session) return;
        timer = window.setTimeout(() => {
          setOpen(true);
          trackEvent("signup_popup_open", {
            popup_id: "signup-popup-v1",
            delay_ms: 5000,
            path: window.location.pathname,
          });
        }, 5000);
      } catch {
        timer = window.setTimeout(() => {
          setOpen(true);
          trackEvent("signup_popup_open", {
            popup_id: "signup-popup-v1",
            delay_ms: 5000,
            path: window.location.pathname,
          });
        }, 5000);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = (method: "close_button" | "backdrop" | "maybe_later" | "cta") => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    trackEvent("signup_popup_dismiss", {
      popup_id: "signup-popup-v1",
      method,
    });
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-popup-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 animate-in fade-in"
      onClick={() => dismiss("backdrop")}
    >
      <div
        className="relative w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-2xl animate-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          onClick={() => dismiss("close_button")}
          className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 id="signup-popup-title" className="font-script text-3xl text-primary">
          Join Surer Adda
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sign up to book Sanchayeeta for your event, save your bookings, and
          get updates on upcoming shows.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Link
            to="/auth"
            onClick={() => {
              trackEvent("signup_popup_cta_click", { popup_id: "signup-popup-v1" });
              dismiss("cta");
            }}
            className="flex-1"
          >
            <Button className="w-full bg-primary text-primary-foreground">
              Sign up free
            </Button>
          </Link>
          <Button variant="outline" className="flex-1" onClick={() => dismiss("maybe_later")}>
            Maybe later
          </Button>
        </div>
      </div>
    </div>
  );
}
